/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { Skill } from '../../../ts/skill';
/**
 * Creates an `<a>` tag and clicks on it
 * download a file
 * @param {string} uri target link
 * @param {string} fileName file name
 *
 * @returns {undefined}
 */
export const saveAs = (uri: string, fileName: string): void => {
  const stubLink = document.createElement('a');
  stubLink.style.display = 'none';
  stubLink.href = uri;
  stubLink.download = fileName;
  document.body.appendChild(stubLink);
  stubLink.click();
  document.body.removeChild(stubLink);
};

/**
 * Stringify `data` object and triggers a save as by calling `saveAs()`
 * @param {Object} data The object to stringify
 * @param {string} fileName file name
 *
 * @returns {undefined}
 */
export const triggerSaveJson = (data: Skill, fileName: string) => {
  const dataAsBlob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json'
  });
  const skillUrl = URL.createObjectURL(dataAsBlob);
  saveAs(skillUrl, fileName);
};
// logic is same as `packages/server-ts/src/routes/rest/content/intents.ts`
// becasue here it is only for exporting intents and entities,
// the original `options.export` is always true
// so I removed some conditions that is not used by the export functionality
// By Xiaoyan Wang (Xiaoyan.Wang1@ibm.com)

export function formatIntents(data) {
  var csvOutput = '';
  if (!_.isEmpty(data)) {
    for (var i = 0; i < data.length; i++) {
      csvOutput += formatIntent(data[i]);
    }
  }
  return csvOutput;
}

export function formatIntent(intent) {
  var intentName = intent.intent;
  var intentExamples = intent.examples;
  var resultEntityString = '';
  if (!intentExamples || intentExamples.length === 0) {
    return ',' + stringifyItem(intentName) + '\r\n';
  }
  for (var i = 0; i < intentExamples.length; i++) {
    resultEntityString +=
      stringifyItem(intentExamples[i].text) +
      ',' +
      stringifyItem(intentName) +
      '\r\n';
  }
  return resultEntityString;
}

export function formatEntities(data) {
  var csvOutput = '';
  if (!_.isEmpty(data)) {
    for (var i = 0; i < data.length; i++) {
      csvOutput += formatEntity(data[i]);
    }
  }
  return csvOutput;
}

export function formatEntity(entity) {
  var entityName = entity.entity;
  var entityValues = entity.values;
  var resultEntityString = '';
  if (!entityValues || entityValues.length === 0) {
    return stringifyItem(entityName) + '\r\n';
  }
  for (var i = 0; i < entityValues.length; i++) {
    resultEntityString +=
      stringifyItem(entityName) + ',' + formatValue(entityValues[i]) + '\r\n';
  }
  return resultEntityString;
}

export function formatValue(value) {
  var valueName = value.value;
  var synonyms = value.synonyms;
  var patterns = value.patterns;
  var resultValueString = stringifyItem(valueName);
  if (synonyms) {
    for (var i = 0; i < synonyms.length; i++) {
      resultValueString += ',' + stringifyItem(synonyms[i]);
    }
  } else if (patterns) {
    var patternsStringified = [];
    for (var l = 0; l < patterns.length; l++) {
      // @ts-ignore
      patternsStringified[l] = stringifyItem('/' + patterns[l] + '/');
    }
    resultValueString += ',' + patternsStringified.join(',');
  }
  return resultValueString;
}

export function stringifyItem(item) {
  var hasQuote = item.indexOf('"') >= 0;
  var hasOthers =
    item.indexOf('\r') >= 0 ||
    item.indexOf('\n') >= 0 ||
    item.indexOf(',') >= 0;
  if (hasQuote) {
    item = item.replace(new RegExp('"', 'g'), '""');
  }
  if (hasOthers || hasQuote) {
    item = '"' + item + '"';
  }
  return item;
}

export function getFormatter(data, type) {
  if (type === 'intents') {
    return formatIntents(data);
  } else {
    return formatEntities(data);
  }
}
